import { t } from "i18next";
import { isNumber } from "lodash";

import {
  IGenericUploadPrepareDto,
  IPassengerInfo,
  IPassengerTooltipInfo,
  IRideDto,
  IRideTableDto,
  IUpdateResultRidesDto,
  SimpleResult,
  TRydeUpdatePrice,
} from "../dto";
import {
  Api,
  getCompanyId,
  getRideBulkRecalculatePricePath,
  getRideBulkRenewPath,
  Log,
} from "../helpers";
import {
  DatesUnit,
  IBulkRideRebuildPayload,
  IBulkUpdateStationsPayload,
  IGenericUploadPrepResult,
  INearbySearchResultDto,
  INeighborAreaDto,
  IPassengerForStationCriteriaDto,
  IPassengerForStationDto,
  IPriceRateSearchDetailsPayload,
  IPriceRateSearchDetailsResult,
  IRecountStationsResultDto,
  IReorderStationsPayload,
  IRideArchiveApiPayload,
  IRideArchiveCancelApiPayload,
  IRideAssignDocksPayload,
  IRideBookingInfoDto,
  IRideBulkRebuildRoutesPayload,
  IRideBulkRecalculatePricePayload,
  IRideBulkUpdatePayload,
  IRideCancelApiPayload,
  IRideMovePassengersApiPayload,
  IRideRebuildPayload,
  IRideRecalculatePricePayload,
  IRideRemoveEmptyStationApiPayload,
  IRideRemovePassengersApiPayload,
  IRideUpdateResultDto,
  IRydeProcess,
  ISearchCriteria,
  ISendSmsOptionsRideModel,
  IUpdateRideStationDto,
  RecountRideStationsCriteriaDto,
  RideEntity,
  RideSoftValidationErrorResponse,
  TRideBulkSoftValidationErrorResponse,
  TRideUpdateDriverOrVehicleDto,
} from "../models";

export class RidesApi {
  static async assignDocks(
    payload: IRideAssignDocksPayload,
    abortController?: AbortController,
  ): Promise<SimpleResult<IRideTableDto[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/assign-docks`;
    const response = await Api.post<SimpleResult<IRideTableDto[]> | undefined>(
      requestUrl,
      payload,
      abortController,
    );

    return response?.data;
  }

  static async searchNearbyRides(
    rideId: number,
    payload: ISearchCriteria<RideEntity>,
    abortController?: AbortController,
  ): Promise<SimpleResult<INearbySearchResultDto> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${rideId}/nearby`;
    const response = await Api.post<
      SimpleResult<INearbySearchResultDto> | undefined
    >(requestUrl, payload, abortController);

    return response?.data;
  }

  static async searchNeighborAreas({
    rideId,
    areaId,
    abortController,
  }: {
    rideId: number;
    areaId: number;
    abortController?: AbortController;
  }): Promise<SimpleResult<number[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${rideId}/neighbor`;
    const response = await Api.post<SimpleResult<number[]> | undefined>(
      requestUrl,
      { areaId },
      abortController,
    );

    return response?.data;
  }

  static async searchNeighborAreasWithMaxPlaces({
    rideId,
    areaId,
    abortController,
  }: {
    rideId: number;
    areaId: number;
    abortController?: AbortController;
  }): Promise<SimpleResult<INeighborAreaDto[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${rideId}/neighbor-places-info`;
    const response = await Api.post<
      SimpleResult<INeighborAreaDto[]> | undefined
    >(requestUrl, { areaId }, abortController);

    return response?.data;
  }

  static async searchNearbyRidesBatch(
    rideIds: number[],
    allowedTimeGapSearching?: DatesUnit,
  ): Promise<SimpleResult<Record<number, number[]>> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/nearby_batch`;
    const response = await Api.post<
      SimpleResult<Record<number, number[]>> | undefined
    >(requestUrl, { rideIds, allowedTimeGapSearching });

    return response?.data;
  }

  static async postRide(
    payload: Partial<IRideDto>,
  ): Promise<
    SimpleResult<IRideDto | RideSoftValidationErrorResponse> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides`;
    const response = await Api.post<
      SimpleResult<IRideDto | RideSoftValidationErrorResponse>
    >(requestUrl, payload);
    return response?.data;
  }

  static async patchRide({
    rideId,
    rideForApiSend,
  }: {
    rideId: number;
    rideForApiSend: Partial<IRideDto>;
    isRideFieldsChanged?: boolean;
  }): Promise<
    SimpleResult<IRideDto | RideSoftValidationErrorResponse> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${rideId}`;
    const response = await Api.patch<
      SimpleResult<IRideDto | RideSoftValidationErrorResponse>
    >(requestUrl, rideForApiSend);
    return response?.data;
  }

  static async patchRideFields({
    rideId,
    rideDataForApiSend,
  }: {
    rideId: number;
    rideDataForApiSend: TRideUpdateDriverOrVehicleDto;
  }): Promise<
    SimpleResult<IRideDto | RideSoftValidationErrorResponse> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${rideId}/ride-fields`;
    const response = await Api.patch<
      SimpleResult<IRideDto | RideSoftValidationErrorResponse>
    >(requestUrl, rideDataForApiSend);
    return response?.data;
  }

  static async cancelRide(
    id: number,
    payload: IRideCancelApiPayload,
  ): Promise<
    SimpleResult<IRideDto | RideSoftValidationErrorResponse> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${id}/cancel`;
    const response = await Api.patch<
      SimpleResult<IRideDto | RideSoftValidationErrorResponse>
    >(requestUrl, payload);

    return response?.data;
  }

  static async deleteRide(
    id: number,
    payload: IRideArchiveApiPayload,
  ): Promise<
    SimpleResult<number | RideSoftValidationErrorResponse> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${id}`;
    const response = await Api.delete<
      SimpleResult<number | RideSoftValidationErrorResponse>
    >(requestUrl, payload);

    return response?.data;
  }

  static async fetchRide(id: number): Promise<IRideTableDto | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/${id}`;
    const response = await Api.get<IRideTableDto>(requestUrl);

    return response?.data;
  }

  static async movePassengers(
    payload: IRideMovePassengersApiPayload,
  ): Promise<
    | SimpleResult<
        IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
      >
    | undefined
  > {
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/rides/move-passengers`;
    const response = await Api.patch<
      SimpleResult<
        IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
      >
    >(requestUrl, payload);

    return response?.data;
  }

  static async removePassengersFromRide(
    rideId: number,
    payload: IRideRemovePassengersApiPayload,
  ): Promise<
    SimpleResult<IRideTableDto | RideSoftValidationErrorResponse> | undefined
  > {
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/rides/${rideId}/passengers`;
    const response = await Api.delete<
      SimpleResult<IRideTableDto | RideSoftValidationErrorResponse> | undefined
    >(requestUrl, payload);

    return response?.data;
  }

  static async removeEmptyStationFromRide(
    rideId: number,
    payload: IRideRemoveEmptyStationApiPayload,
  ): Promise<SimpleResult<IRideTableDto> | undefined> {
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/rides/${rideId}/empty-station`;
    const response = await Api.delete<SimpleResult<IRideTableDto>>(
      requestUrl,
      payload,
    );

    return response?.data;
  }

  static async updateRideStationAttributes(
    rideId: number,
    payload: Partial<IUpdateRideStationDto>,
  ): Promise<SimpleResult<IRideTableDto> | undefined> {
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/rides/${rideId}/station`;
    const response = await Api.patch<SimpleResult<IRideTableDto>>(
      requestUrl,
      payload,
    );

    return response?.data;
  }

  static async reorderStations(
    payload: IReorderStationsPayload,
  ): Promise<
    SimpleResult<IRideTableDto | RideSoftValidationErrorResponse> | undefined
  > {
    const { rideId } = payload;
    const requestUrl = `/api/v1/core/c/${getCompanyId()}/rides/${rideId}/stations`;

    const response = await Api.patch<
      SimpleResult<IRideTableDto | RideSoftValidationErrorResponse>
    >(requestUrl, payload);

    return response?.data;
  }

  static async fetchPassForDropdownWithStations(
    criteria?: IPassengerForStationCriteriaDto,
  ): Promise<SimpleResult<IPassengerForStationDto[]> | undefined> {
    const response = await Api.post<SimpleResult<IPassengerForStationDto[]>>(
      `/api/v1/core/c/${getCompanyId()}/passengers/list-dropdown-dtos-with-stations`,
      criteria,
    );

    return response?.data;
  }

  static async fetchListPassengersInfos(
    ids: number[],
  ): Promise<IPassengerInfo[] | undefined> {
    const companyId = getCompanyId();

    if (!ids?.length || !Array.isArray(ids)) {
      Log.warnAndToast(t("errors.noIdsToFetchPassengers"));
      return undefined;
    }

    const response = await Api.post<IPassengerInfo[]>(
      `/api/v1/core/c/${companyId}/passengers/list-pass-infos`,
      { ids },
    );

    return response?.data;
  }

  static async fetchPassengerTooltipInfos(
    ids: number[],
    abortController?: AbortController,
  ): Promise<SimpleResult<IPassengerTooltipInfo[]> | undefined> {
    const companyId = getCompanyId();

    if (!ids?.length || !Array.isArray(ids)) {
      return undefined;
    }

    const response = await Api.post<SimpleResult<IPassengerTooltipInfo[]>>(
      `/api/v1/core/c/${companyId}/passengers/list-tooltip-infos`,
      { ids },
      abortController,
    );

    return response?.data;
  }

  static async recountStationInfo(
    criteria: RecountRideStationsCriteriaDto,
  ): Promise<
    | SimpleResult<IRecountStationsResultDto | RideSoftValidationErrorResponse>
    | undefined
  > {
    const { stations, rideStart } = criteria;

    if (!stations?.length || !rideStart) {
      Log.warnAndToast(t("errors.invalidDataForRecountNoStationOfDate"));
      return new SimpleResult({
        error: t("errors.invalidDataForRecountNoStationOfDate"),
      });
    }

    const response = await Api.post<
      | SimpleResult<
          IRecountStationsResultDto | RideSoftValidationErrorResponse
        >
      | undefined
    >(`/api/v1/core/c/${getCompanyId()}/rides/recount-stations`, criteria);

    return response?.data;
  }

  static async count(payload: ISearchCriteria<IRideTableDto>): Promise<number> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/count`;
    const response = await Api.post<boolean>(requestUrl, payload);

    if (isNumber(response?.data)) {
      return response?.data;
    }

    Log.error(t("errors.wrongResponseTypeFromRideCount"), response?.data);

    return 0;
  }

  static async sendSms(
    payload: ISendSmsOptionsRideModel,
  ): Promise<SimpleResult<void> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/map/sms`;
    const response = await Api.post<SimpleResult<void>>(requestUrl, payload);
    return response?.data;
  }

  static async bulkCancelRides(
    payload: IRideArchiveCancelApiPayload,
  ): Promise<
    | SimpleResult<IRydeProcess | TRideBulkSoftValidationErrorResponse>
    | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/bulk/cancel`;

    const response = await Api.patch<
      | SimpleResult<IRydeProcess | TRideBulkSoftValidationErrorResponse>
      | undefined
    >(requestUrl, payload);
    return response?.data;
  }

  static async bulkRenewRides(payload: {
    ids: number[];
  }): Promise<SimpleResult<TRydeUpdatePrice[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = getRideBulkRenewPath(companyId || 0);
    const renewRidesResponse = await Api.patch<
      SimpleResult<IRideTableDto[]> | undefined
    >(requestUrl, payload);

    return renewRidesResponse?.data;
  }

  static async bulkArchiveRides(
    payload: IRideArchiveCancelApiPayload,
  ): Promise<
    | SimpleResult<IRydeProcess | TRideBulkSoftValidationErrorResponse>
    | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/bulk/archive`;

    const response = await Api.delete<
      | SimpleResult<IRydeProcess | TRideBulkSoftValidationErrorResponse>
      | undefined
    >(requestUrl, payload);
    return response?.data;
  }

  static async bulkRebuildRoutesRides(
    payload: IRideBulkRebuildRoutesPayload,
  ): Promise<
    SimpleResult<object | TRideBulkSoftValidationErrorResponse> | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/bulk/rebuild-route`;

    const response = await Api.patch<
      SimpleResult<TRideBulkSoftValidationErrorResponse> | undefined
    >(requestUrl, payload);
    return response?.data;
  }

  static async bulkRideRebuild(
    payload: IBulkRideRebuildPayload,
  ): Promise<SimpleResult<IRydeProcess> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/bulk/ride-rebuild`;

    const response = await Api.post<SimpleResult<IRydeProcess> | undefined>(
      requestUrl,
      { payload },
    );
    return response?.data;
  }

  static async bulkUpdateStations(
    payload: IBulkUpdateStationsPayload,
  ): Promise<SimpleResult<IRydeProcess> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/bulk/update-stations`;

    const response = await Api.post<SimpleResult<IRydeProcess> | undefined>(
      requestUrl,
      { payload },
    );
    return response?.data;
  }

  static async bulkUpdateRides(
    payload: IRideBulkUpdatePayload,
  ): Promise<
    | SimpleResult<IUpdateResultRidesDto | TRideBulkSoftValidationErrorResponse>
    | undefined
  > {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/bulk/update`;

    const response = await Api.patch<
      SimpleResult<TRideBulkSoftValidationErrorResponse> | undefined
    >(requestUrl, payload);
    return response?.data;
  }

  static async bulkRecalculateRidePrice(
    payload: IRideBulkRecalculatePricePayload,
  ): Promise<SimpleResult<TRydeUpdatePrice[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = getRideBulkRecalculatePricePath(companyId || 0);
    const recalculatePriceResponse = await Api.patch<
      SimpleResult<TRydeUpdatePrice[]> | undefined
    >(requestUrl, payload);

    return recalculatePriceResponse?.data;
  }

  static async recalculateRidePrice(
    payload: IRideRecalculatePricePayload,
  ): Promise<SimpleResult<TRydeUpdatePrice> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/recalculate-ride-price`;
    const recalculatePriceResponse = await Api.post<
      SimpleResult<TRydeUpdatePrice> | undefined
    >(requestUrl, payload);

    return recalculatePriceResponse?.data;
  }

  static async getPriceRateSearchDetails(
    rideId: number,
    options?: Omit<IPriceRateSearchDetailsPayload, "rideId"> & {
      abortController?: AbortController;
    },
  ): Promise<SimpleResult<IPriceRateSearchDetailsResult> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/price-rate-search-details`;
    const payload: IPriceRateSearchDetailsPayload = {
      rideId,
      explainMode: options?.explainMode,
      isProduction: options?.isProduction,
      supplierId: options?.supplierId,
      vehicleTypeId: options?.vehicleTypeId,
      lockSupplier: options?.lockSupplier,
      lockVehicleType: options?.lockVehicleType,
      lockCurrentPriceRateIfExists: options?.lockCurrentPriceRateIfExists,
    };
    const response = await Api.post<
      SimpleResult<IPriceRateSearchDetailsResult> | undefined
    >(requestUrl, payload, options?.abortController);

    return response?.data;
  }

  static async recalculateSupplierPriceRate(
    payload: IRideRecalculatePricePayload,
  ): Promise<SimpleResult<TRydeUpdatePrice> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/recalculate-supplier-price-rate`;
    const recalculatePriceResponse = await Api.post<
      SimpleResult<TRydeUpdatePrice> | undefined
    >(requestUrl, payload);

    return recalculatePriceResponse?.data;
  }

  static async searchRideBookings(
    rideId: number,
    abortController?: AbortController,
  ): Promise<SimpleResult<IRideBookingInfoDto[]> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/searchRideBookings`;
    const response = await Api.post<SimpleResult<IRideBookingInfoDto[]>>(
      requestUrl,
      { rideId },
      abortController,
    );

    return response?.data;
  }

  static async rideRebuild(
    payload: IRideRebuildPayload,
  ): Promise<SimpleResult<TRydeUpdatePrice> | undefined> {
    const companyId = getCompanyId();
    const requestUrl = `/api/v1/core/c/${companyId}/rides/ride-rebuild`;
    const recalculatePriceResponse = await Api.post<
      SimpleResult<TRydeUpdatePrice> | undefined
    >(requestUrl, payload);

    return recalculatePriceResponse?.data;
  }

  static async ridesUploadPrepare({
    body,
    abortController,
  }: {
    body: IGenericUploadPrepareDto;
    abortController?: AbortController;
  }): Promise<SimpleResult<IGenericUploadPrepResult> | undefined> {
    const companyId = getCompanyId();
    const url = `/api/v1/core/c/${companyId}/rides-upload/prepare`;
    const response = await Api.postExtended<
      SimpleResult<IGenericUploadPrepResult>
    >({ url, body, abortController });
    return response?.data;
  }
}
